import Card from '../components/card/Card';
import P from '../components/texts/p/P';
import Icon from '../components/icon/Icon';
import Badge from '../components/badge/Badge';
import { getInstallmentBadgeStatus } from './MonetizationUtils';
import {
  formatInstallmentStatusLabel,
  formatOriginalDueDateDisplay,
  shouldShowInstallmentEarlyCancellationIcon,
} from './earlyCancellationUtils';

// Función para formatear fecha de YYYY-MM-DD a DD/MM/YYYY
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${day}/${month}/${year}`;
};

// Función para formatear números con separador de miles
const formatNumber = (value: number): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export interface InstallmentData {
  installment_number: number;
  due_date: string;
  original_due_date?: string | null;
  amortization: number;
  interest: number;
  amount: number;
  remaining_amount: number;
  due_days: number;
  status_type: string;
  /** PK de la cuota (no confundir con id del préstamo usado por OverdueStatusModal). */
  installment_id?: number;
  // Campos adicionales para compatibilidad con OverdueStatusModal
  id?: number;
  id_global?: string;
  type?: string;
  application_product_line_id?: number;
  days_overdue?: number;
  creditor_name?: string;
  currency?: string;
  rate_type_id?: number;
  rate_percentage?: number;
  cuit?: string;
  company_name?: string;
  monetization_amount?: number;
  total_installments?: number;
  monetization_status_type_id?: number;
}

interface GetInstallmentColumnsProps {
  handleOpenModal: (row: InstallmentData) => void;
  handleEarlyCancellation?: (row: InstallmentData) => void;
  /** Solo mostrar acción habilitada si el aval está EJECUTADA (monetization_status_type_id = 2). */
  earlyCancellationEnabled?: boolean;
}

export function getInstallmentColumns({
  handleOpenModal,
  handleEarlyCancellation,
  earlyCancellationEnabled = false,
}: GetInstallmentColumnsProps) {
  return [
    {
      name: 'Nº cuota',
      selector: (row: InstallmentData) => row.installment_number,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.installment_number}
          </P>
        </Card>
      ),
      minWidth: '90px',
      grow: 0.5,
    },
    {
      name: 'F. vencimiento',
      selector: (row: InstallmentData) => row.due_date,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatDate(row.due_date)}
          </P>
        </Card>
      ),
      minWidth: '120px',
      grow: 0.7,
    },
    {
      name: 'F. vto original',
      selector: (row: InstallmentData) => row.original_due_date || '',
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatOriginalDueDateDisplay(row.original_due_date)}
          </P>
        </Card>
      ),
      minWidth: '120px',
      grow: 0.7,
    },
    {
      name: 'Amortización',
      selector: (row: InstallmentData) => row.amortization,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.amortization)}
          </P>
        </Card>
      ),
      minWidth: '120px',
      grow: 0.8,
    },
    {
      name: 'Interés',
      selector: (row: InstallmentData) => row.interest,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.interest)}
          </P>
        </Card>
      ),
      minWidth: '120px',
      grow: 0.8,
    },
    {
      name: 'Total cuota',
      selector: (row: InstallmentData) => row.amount,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.amount)}
          </P>
        </Card>
      ),
      minWidth: '120px',
      grow: 0.8,
    },
    {
      name: 'Saldo Final',
      selector: (row: InstallmentData) => row.remaining_amount,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.remaining_amount)}
          </P>
        </Card>
      ),
      minWidth: '120px',
      grow: 0.8,
    },
    {
      name: 'Días vencida',
      selector: (row: InstallmentData) => row.due_days,
      cell: (row: InstallmentData) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.due_days || '-'}
          </P>
        </Card>
      ),
      minWidth: '100px',
      grow: 0.6,
    },
    {
      name: 'Estado',
      selector: (row: InstallmentData) => row.status_type,
      cell: (row: InstallmentData) => {
        const badgeVariant = getInstallmentBadgeStatus(row?.status_type);
        return (
          <Card>
            <Badge variant={badgeVariant} type='text'>
              {formatInstallmentStatusLabel(row.status_type)}
            </Badge>
          </Card>
        );
      },
      minWidth: '140px',
      grow: 0.8,
    },
    {
      name: 'Acciones',
      cell: (row: InstallmentData) => {
        const isEligible =
          earlyCancellationEnabled &&
          (row.status_type || '').toLowerCase() === 'pendiente' &&
          !row.original_due_date;
        const showEarlyCancellationIcon = shouldShowInstallmentEarlyCancellationIcon({
          earlyCancellationEnabled,
          statusType: row.status_type,
        });
        return (
          <Card display='flex' justifyContent='center' gap='8px' alignItems='center'>
            {showEarlyCancellationIcon && handleEarlyCancellation && (
              <Icon
                name='cancel_circle_outline'
                variant='warning'
                width={20}
                height={20}
                cursor={isEligible ? 'pointer' : 'default'}
                disabled={!isEligible}
                aria-label={
                  isEligible
                    ? 'Cancelación anticipada'
                    : 'Cuota cancelada anticipadamente'
                }
                onClick={isEligible ? () => handleEarlyCancellation(row) : undefined}
              />
            )}
            <Icon
              name='arrow_right_outline'
              variant='info'
              width={20}
              height={20}
              cursor='pointer'
              onClick={() => handleOpenModal(row)}
            />
          </Card>
        );
      },
      minWidth: '120px',
      grow: 0.5,
    },
  ];
}
